import React, { useEffect } from "react";
import { Box, Button, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

// A hard, multi-directional outline (not just one soft blurred shadow) —
// some archive photos have their own bold white lettering baked in (e.g. a
// sponsor banner), and white text-on-white text doesn't get more legible no
// matter how blurred a single shadow is. An outline gives every glyph a
// dark edge that survives even that collision.
const heroTextShadow = [
    "-1px -1px 2px rgba(0,0,0,0.95)",
    "1px -1px 2px rgba(0,0,0,0.95)",
    "-1px 1px 2px rgba(0,0,0,0.95)",
    "1px 1px 2px rgba(0,0,0,0.95)",
    "0 2px 12px rgba(0,0,0,0.9)",
].join(", ");

// `ctaLabel`/`ctaTo` are optional — only Home currently passes them, since a
// "become a member" call-to-action makes sense on the landing page but not
// on every page that reuses this header (About, Team, Games, ...).
const ImageHeaderBox = ({ title, subTitle, message = null, image = null, ctaLabel = null, ctaTo = null }) => {

    const theme = useTheme();

    const [imageSource, setImageSource] = React.useState(null);

    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const isSm = useMediaQuery(theme.breakpoints.down('md'));
    const isMd = useMediaQuery(theme.breakpoints.down('lg'));
    const isLg = useMediaQuery(theme.breakpoints.down('xl'));
    const imageHeight = isXs ? 320 : (isSm ? 380 : (isMd ? 440 : (isLg ? 500 : 560)));

    const getRandomImageUrl = () => {
        const totalPhotosAvailable = 30;
        const randNr = Math.min(Math.floor(Math.random() * (totalPhotosAvailable + 1)), totalPhotosAvailable);
        const imageNr = `0${randNr}`.slice(-2);
        return `${process.env.PUBLIC_URL}/img/carousel/image-${imageNr}.jpeg`;
    }

    useEffect(() => {
        setImageSource(Boolean(image) ? image : getRandomImageUrl());
    }, [image]);

    const messageSize = isXs ? "1rem" : (isSm ? "1.4rem" : (isMd ? "1.6rem" : (isLg ? "1.8rem" : "2rem")));

    return (
        <React.Fragment>
            <Box
                sx={{
                    position: 'relative',
                    height: `${imageHeight}px`,
                    backgroundImage: `url(${imageSource})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                {/* A bottom-weighted gradient looked more deliberate than a
                    flat tint, but it assumed the text sits at the bottom of
                    the box — it's actually vertically centered as a group,
                    so on pages with a short title + no subtitle/message
                    (most of them) the text lands in the gradient's lighter
                    top region. Over a bright/busy photo that's illegible.
                    A flat, sufficiently dark wash (not a positional
                    gradient) guarantees contrast regardless of where the
                    text block ends up or what's behind it — the text-shadow
                    below is the second, independent line of defense for the
                    same problem (e.g. the cursive "message" variant, which
                    is inherently lower-contrast than a bold sans title).

                    IMPORTANT: `theme.palette.X.dark` here is a MUI-computed
                    `rgb(...)`/`rgba(...)` string, never hex (verified:
                    `createTheme(...).palette.primary.dark` →
                    `"rgb(31, 32, 91)"`) — appending a hex alpha suffix like
                    `${...}CC` produces an invalid CSS color
                    (`"rgb(31, 32, 91)CC"`), which the browser just drops,
                    silently leaving the overlay fully transparent. Both the
                    gradient version and the first flat-wash version of this
                    fix had exactly that bug — it went unnoticed in testing
                    because the sample photos checked were already dark/muted
                    enough that "no overlay" still looked plausible. Always
                    use MUI's own `alpha()` helper to add opacity to a theme
                    color; it decomposes whatever format the string is in
                    and recomposes it correctly. */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: alpha(theme.palette.raisinBlack.dark, 0.72),
                    }}
                >
                    <Box height="100%" display="flex" alignItems="center" justifyContent="center" flexDirection="column" sx={{ px: theme.spacing(4) }}>
                        <Typography
                            fontWeight="bold"
                            variant="h1"
                            color={theme.palette.whiteSmoke.dark}
                            sx={{
                                fontVariant: "all-small-caps",
                                borderBottom: `7px solid ${theme.palette.maize.dark}`,
                                textShadow: heroTextShadow,
                            }}
                        >
                            {title}
                        </Typography>
                        {
                            subTitle ? (
                                <Typography
                                    variant="h2"
                                    color={theme.palette.whiteSmoke.dark}
                                    sx={{
                                        fontVariant: "all-small-caps",
                                        textShadow: heroTextShadow,
                                    }}
                                >
                                    {subTitle}
                                </Typography>
                            ) : (
                                Boolean(message) ? (
                                    <Container maxWidth="md">
                                        <Typography
                                            color={theme.palette.whiteSmoke.dark}
                                            sx={{
                                                fontFamily: "Caveat",
                                                fontSize: messageSize,
                                                textShadow: heroTextShadow,
                                            }}
                                        >
                                            {message}
                                        </Typography>
                                    </Container>
                                ) : null
                            )
                        }
                        {
                            Boolean(ctaLabel) && Boolean(ctaTo) ? (
                                <Button
                                    component={RouterLink}
                                    to={ctaTo}
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        mt: theme.spacing(6),
                                        backgroundColor: theme.palette.maize.dark,
                                        color: theme.palette.raisinBlack.dark,
                                        fontWeight: "bold",
                                        textTransform: "none",
                                        fontSize: "1.05rem",
                                        px: theme.spacing(6),
                                        "&:hover": { backgroundColor: theme.palette.saffron.dark },
                                    }}
                                >
                                    {ctaLabel}
                                </Button>
                            ) : null
                        }
                    </Box>
                </Box>
            </Box>
        </React.Fragment>
    );
}

export default ImageHeaderBox;
